import api from '@/api';
import { PageResult } from '@/core/request/PageResult';
import { PageRequest } from '@/core/request/PageRequest';
import {
  Employee,
  EmployeeAddress,
  EmployeeContact,
  EmployeeContactCreate,
  EmployeeContactEdit,
  EmployeeEditFormValues,
  EmployeeInfo,
  EmployeeIsEstimator,
  EmployeeProfessional,
  EmployeeProfessionalCreate,
  EmployeeReduced,
  ListEmployeesQueryReduced,
} from './Employees.types';
import { API_BASE_URL } from '..';

const CONTROLLER_URL = `${API_BASE_URL}/employees`;

export async function listEmployees(
  params?: PageRequest,
): Promise<PageResult<Employee>> {
  const result = (await api.get(`${CONTROLLER_URL}/matrix`, params)) as unknown;

  return result as PageResult<Employee>;
}

export async function listEmployeesReduced(params?: ListEmployeesQueryReduced) {
  const result = (await api.get(
    `${CONTROLLER_URL}/matrix/reduced`,
    params,
  )) as unknown;

  return result as PageResult<EmployeeReduced>;
}

export async function getEmployee(id: number): Promise<Employee> {
  const result = (await api.get(`${CONTROLLER_URL}/${id}`, null)) as unknown;

  return result as Employee;
}

export async function getEmployeeInfo(id: number): Promise<EmployeeInfo> {
  const result = (await api.get(
    `${CONTROLLER_URL}/${id}/info`,
    null,
  )) as unknown;

  return result as EmployeeInfo;
}

export async function getEmployeeIsEstimator(id: number) {
  const result = (await api.get(
    `${CONTROLLER_URL}/${id}/isEstimator`,
    null,
  )) as unknown;

  return result as EmployeeIsEstimator;
}

export async function getEmployeeReduced(id: number): Promise<EmployeeReduced> {
  const result = (await api.get(
    `${CONTROLLER_URL}/${id}/reduced`,
    null,
  )) as unknown;

  return result as EmployeeReduced;
}

export async function employeeCreate(data: Employee): Promise<Employee> {
  const result = (await api.post(`${CONTROLLER_URL}`, data, null)) as unknown;

  return result as Employee;
}

export async function employeeUpdate(
  data: EmployeeEditFormValues,
): Promise<Employee> {
  const result = (await api.put(`${CONTROLLER_URL}`, data, null)) as unknown;

  return result as Employee;
}

export async function deleteEmployee(id: number) {
  const result = await api.remove(`${CONTROLLER_URL}/${id}`, {}, null);

  return result;
}

export async function getEmployeeContacts(employeerId: number) {
  const result = (await api.get(
    `${CONTROLLER_URL}/${employeerId}/contacts`,
    null,
  )) as unknown;

  return result as EmployeeContact[];
}

export async function postEmployeeContacts(data: EmployeeContactCreate) {
  const result = (await api.post(
    `${CONTROLLER_URL}/${data.employeeId}/contacts`,
    data,
    null,
  )) as unknown;

  return result as EmployeeContact;
}

export async function putEmployeeContacts(data: EmployeeContactEdit) {
  const result = (await api.put(
    `${CONTROLLER_URL}/${data.employeeId}/contacts/${data.id}`,
    data,
    null,
  )) as unknown;

  return result as EmployeeContact;
}

export async function detailEmployeeContacts(
  employeeId: number,
  contactId: number,
) {
  const result = (await api.get(
    `${CONTROLLER_URL}/${employeeId}/contacts/${contactId}`,
    null,
  )) as unknown;

  return result as EmployeeContact;
}

export async function removeEmployeeContacts(
  employeeId: number,
  contactId: number,
) {
  const result = (await api.remove(
    `${CONTROLLER_URL}/${employeeId}/contacts/${contactId}`,
    {},
    null,
  )) as unknown;

  return result as void;
}

export async function removeEmployeeContactDetail(
  employeeId: number,
  detailId: number,
) {
  const result = (await api.remove(
    `${CONTROLLER_URL}/${employeeId}/contacts/contactDetail/${detailId}`,
    {},
    null,
  )) as unknown;

  return result as void;
}

export async function getEmployeeAddress(employeeId?: number) {
  const result = (await api.get(
    `${CONTROLLER_URL}/${employeeId}/address`,
    {},
  )) as unknown;

  return result as { data: EmployeeAddress[] };
}

export async function postEmployeeAddress(
  data: Omit<EmployeeAddress, 'id'> & { employeeId: number },
) {
  const result = (await api.post(
    `${CONTROLLER_URL}/${data.employeeId}/address`,
    data,
    null,
  )) as unknown;

  return result as EmployeeAddress;
}

export async function putEmployeeAddress(
  data: EmployeeAddress & { employeeId: number },
) {
  const result = (await api.put(
    `${CONTROLLER_URL}/${data.employeeId}/address`,
    data,
    null,
  )) as unknown;

  return result as EmployeeAddress;
}

export async function removeEmployeeAddress(
  employeeId: number,
  addressId: number,
) {
  const result = (await api.remove(
    `${CONTROLLER_URL}/${employeeId}/address/${addressId}`,
    {},
    null,
  )) as unknown;

  return result as void;
}

export async function detailEmployeeProfessional(employeeId: number) {
  const result = (await api.get(
    `${CONTROLLER_URL}/${employeeId}/professional-information`,
    null,
  )) as unknown;

  return result as EmployeeProfessional;
}

export async function createEmployeeProfessional(
  data: EmployeeProfessionalCreate,
) {
  const result = (await api.post(
    `${CONTROLLER_URL}/${data.employeeId}/professional-information`,
    data,
    null,
  )) as unknown;

  return result as EmployeeProfessional;
}

export async function editEmployeeProfessional(data: EmployeeProfessional) {
  const result = (await api.put(
    `${CONTROLLER_URL}/${data.employeeId}/professional-information/${data.id}`,
    data,
    null,
  )) as unknown;

  return result as EmployeeProfessional;
}

export async function removeEmployeeProfessional(employeeId: number) {
  const result = (await api.remove(
    `${CONTROLLER_URL}/${employeeId}/professional-information`,
    {},
    null,
  )) as unknown;

  return result as void;
}

export async function grantAccessToEmployee(employeeId?: number) {
  const result = (await api.post(
    `${CONTROLLER_URL}/${employeeId}/productive-access`,
    {},
    null,
  )) as unknown;

  return result as void;
}
